import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { IconButton, Stack, Typography } from "@mui/material";
import { RoleComponentGuard } from "components/guards/Role-component.guard";
import { AddTagModal, ParsingPdfModal, TagNewPdfModal, TagNewPdfModalProps } from "components/modals";
import { Notice } from "components/Prompts/Notice";
import { AddFilesButton, DocumentView, HintPopover, TableDrawer } from "components/Tagger";
import { SmartTagBar } from "components/Tagger/SmartTagBar/SmartTagBar";
import dayjs from "dayjs";
import { useActionHook, useIsViewerPermission, useModals, useNavigate, usePrompt, useUrlHooks } from "hooks";
import { isArray } from "lodash";
import Papa from "papaparse";
import qs, { ParsedQs } from "qs";
import { useTaggerQueries } from "queries/query-context";
import {
  IApiServiceError,
  useAccountSelector,
  useDealActions,
  useFileScanningSelector,
  useTaggerActions,
  useTaggerAnalyzingStatusSelector,
  useTaggerDealIdSelector,
  useTaggerDocumentArraySelector,
  useTaggerDocumentParsingSelector,
  useTaggerSelector,
  useTaggerTagTableSelector
} from "store";
import { AltiusErrors, ApiValidationErrors, TagTableRow } from "store/types";
import { Userpilot } from "userpilot";
import downloadFile from "utils/download-file";

import { RoleName } from "../../constants";
import { useTextractSocketHook } from "../../hooks/socket-hooks/use-textract-socket-hook";

export const Tagger: React.FC = () => {
  const documents = useTaggerDocumentArraySelector();
  const fileScanningEnabled = useFileScanningSelector();
  const dealId = useTaggerDealIdSelector();
  const taggerState = useTaggerSelector();

  const { queryParams } = useUrlHooks();

  const { saveTags, addFiles, resetTagger, startAnalysis, undoAnalysis, setAnalyzingStatus } = useTaggerActions();
  const { uploadFileAction, getFileAction } = useDealActions();
  const { getDocumentData } = useTaggerQueries();
  const [isTableOpen, setIsTableOpen] = useState<boolean>(false);
  const [newPdfModalOpen, setNewPdfModalModalOpen] = useState<boolean>(false);
  const [hideParsingModal, setHideParsingModal] = useState<boolean>(false);
  const [hintOpen, setHintOpen] = useState<boolean>(false);
  const { errorModal, successModal, pdfTaggerErrorModal, selectDealFolderModal } = useModals();
  const openPrompt = usePrompt();
  const filesRef = useRef<File[]>([]);
  const hintAnchorRef = useRef<any>(null);
  const parsingPdf = useTaggerDocumentParsingSelector();
  const analyzingStatus = useTaggerAnalyzingStatusSelector();
  const { search } = useLocation();
  const navigate = useNavigate();
  const tableRows = useTaggerTagTableSelector();
  const allowGpt = useAccountSelector()?.allow_gpt || undefined;
  const [scanningFiles, setScanningFiles] = useState<boolean>(false);

  const isAnalystViewerPermission = useIsViewerPermission("file_upload");
  const subscribed = useTextractSocketHook();

  const { action: fileUpload } = useActionHook(uploadFileAction, {
    onSuccess: (res) => {
      Userpilot.track("Upload file to Folder");
      fileScanningEnabled && !scanningFiles && setScanningFiles(res.state === "scanning");
    }
  });

  const [currentDocIndex, setCurrentDocIndex] = useState<number>(0);

  useEffect(() => {
    const urlValidity = (parsedUrl: ParsedQs) => {
      let valid = true;
      const dealId = Number(parsedUrl["dealId"]);
      const dealIds = parsedUrl["dealIds"];
      const filesIds = parsedUrl["filesIds"];
      const section = parsedUrl["section"];
      const sections = parsedUrl["sections"];

      const dealIdValid = !!(dealId && !isNaN(dealId));
      const dealIdsValid = !!(dealIds && isArray(dealIds));
      const sectionsValid = !!(sections && isArray(sections));
      valid = valid && (dealIdValid || dealIdsValid);
      valid = valid && (!section || !isNaN(Number(section)));
      valid = valid && !!(filesIds && isArray(filesIds));
      valid = valid && (dealIdsValid ? dealIds?.length === filesIds?.length : valid);
      valid = valid && (sectionsValid ? sections?.length === filesIds?.length : valid);
      return valid;
    };

    if (subscribed) {
      (async () => {
        const params = new URLSearchParams(search);
        const parsedUrl = qs.parse(search.slice(1));
        if (urlValidity(parsedUrl)) {
          const dealId = Number(params.get("dealId"));
          const dealIds = parsedUrl["dealIds"] as unknown as number[] | undefined;
          const filesIds: number[] = parsedUrl["filesIds"] as unknown as number[];
          const isFileFromNotification = Number(params.get("taggerFile"));
          const sections: number[] = parsedUrl["section"]
            ? [parsedUrl["section"] as unknown as number]
            : (parsedUrl["sections"] as unknown as number[]) || [];

          try {
            let files: Array<[Blob, string, string | null]>;
            if (isFileFromNotification) {
              files = await Promise.all(
                filesIds.map(async (id) => {
                  const document = await getDocumentData(String(id));
                  const res = await fetch(document.pdf_url);
                  if (res.ok) {
                    const blob = await res.blob();
                    return [blob, document.file_name, document.series.series_name];
                  }
                  throw "File can not be processed";
                })
              );
            } else {
              files = await Promise.all(
                filesIds.map(async (id, index) => {
                  const effectiveDealId = dealId || dealIds![index];
                  const file = await getFileAction({ dealId: effectiveDealId, fileId: Number(id) });
                  if (file.file_url) {
                    const res = await fetch(file.file_url);
                    if (res.ok) {
                      const blob = await res.blob();
                      return [blob, file.name, file.document_type];
                    }
                  }
                  throw "File can not be processed";
                })
              );
            }
            await addFiles(
              files.map(([file, filename], index) => ({
                file: new File([file as Blob], filename as string),
                dealId: dealId || dealIds![index],
                folderId: Number(sections[index]) || 0,
                fileId: filesIds ? Number(filesIds[index]) : undefined
              }))
            );
          } catch (e: any) {
            const message = typeof e === "string" ? e : e.errors ? JSON.stringify(e.errors) : "Error!";
            errorModal({ msg: message });
            console.error(e);
          }
        }
      })();
    }
  }, [subscribed]);

  useEffect(() => {
    return () => {
      resetTagger();
    };
  }, []);

  const onFileUpload = useCallback(
    async ({
      dealId,
      folderId,
      documentType,
      investorId,
      targetFiles
    }: {
      dealId: number;
      folderId?: number;
      documentType?: string;
      investorId?: number;
      targetFiles: File[];
    }) => {
      const uploadResults = [];
      for (const file of targetFiles) {
        try {
          const result = await fileUpload({
            file,
            deal_id: dealId,
            ...(folderId && { section_id: folderId }),
            ...(documentType && { document_type: documentType }),
            ...(investorId && { investor_id: investorId })
          });
          uploadResults.push({ ...result, file });
        } catch (error) {
          errorModal({ msg: (error as IApiServiceError).message });
        }
      }
      return uploadResults || [];
    },
    [fileUpload]
  );

  const handleUploadAndAddFiles = useCallback(
    async ({
      dealId,
      folderId,
      documentType,
      investorId,
      targetFiles
    }: {
      dealId: number;
      folderId?: number;
      documentType?: string;
      investorId?: number;
      targetFiles: File[];
    }) => {
      const selectedFiles = await onFileUpload({
        dealId: dealId,
        ...(folderId && { folderId: folderId }),
        ...(documentType && { documentType: documentType }),
        ...(investorId && { investorId: investorId }),
        targetFiles: targetFiles
      });

      const filesToAdd = selectedFiles.map((item) => ({
        file: item.file,
        fileId: item.id,
        dealId: dealId,
        folderId: item.section_id
      }));

      // Retry mechanism for 412 status code errors and network errors
      const maxRetries = 20;
      const retryDelay = 3000; // 3 seconds
      const maxTotalTime = 60000; // 60 seconds
      const startTime = Date.now();

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          await addFiles(filesToAdd);
          setScanningFiles(false);
          return; // Success, exit the retry loop
        } catch (e: any) {
          const error = e as IApiServiceError;
          const is412Error = error?.status === 412;
          // Also retry on network errors (when status is undefined and response is undefined)
          const isNetworkError = error?.status === undefined && error?.response === undefined;
          const shouldRetry = is412Error || isNetworkError;
          const elapsedTime = Date.now() - startTime;
          const isLastAttempt = attempt === maxRetries - 1;

          // If it's not a retryable error, or this is the last attempt, show error and exit
          if (!shouldRetry || isLastAttempt) {
            console.log(e);
            setScanningFiles(false);
            errorModal({ msg: "File can not be processed" });
            return;
          }

          // Check if we would exceed max time after waiting
          if (elapsedTime + retryDelay >= maxTotalTime) {
            console.log(e);
            setScanningFiles(false);
            errorModal({ msg: "File can not be processed" });
            return;
          }

          // Wait 2 seconds before retrying
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      }
    },
    [onFileUpload, addFiles, errorModal, setScanningFiles]
  );

  const handleNext: TagNewPdfModalProps["onNext"] = async (deal, documentType, investorId) => {
    if (!documentType) {
      selectDealFolderModal({
        isOpen: true,
        title: "Select Folder",
        preselectedDeal: { id: deal.value, title: deal.label },

        onSubmit: async ({ selectedFolder }) => {
          handleUploadAndAddFiles({
            dealId: deal.value,
            folderId: selectedFolder?.id,
            targetFiles: filesRef.current
          });
        }
      });
    } else {
      await handleUploadAndAddFiles({
        dealId: deal.value,
        documentType: documentType,
        investorId: investorId,
        targetFiles: filesRef.current
      });
    }
  };

  const handleLoadFiles = (files: File[]) => {
    setNewPdfModalModalOpen(true);
    filesRef.current = files;
  };

  const handleAddTag = async () => {
    await openPrompt({
      Component: AddTagModal,
      props: {}
    });
  };

  const handleEditTag = async (deal: TagTableRow["deal"], tagId: number, is_child: boolean, hasChildren: boolean) => {
    await openPrompt({
      Component: AddTagModal,
      props: {
        tag: { id: tagId, deal, is_child, hasChildren }
      }
    });
  };

  const handleDownload = () => {
    const csv = Papa.unparse(
      tableRows.map((data) => ({
        date: dayjs(data.date).format("YYYY-MM-DD"),
        parameter: data.measure.name,
        deal: data.deal.title,
        investor: data.investor?.name,
        value: data.value
      }))
    );

    downloadFile(csv, "data.csv", "text/csv");
  };

  const handleSave = () => {
    saveTags(taggerState)
      .then(() => {
        successModal({ msg: "Data Saved!" });
        if (queryParams["aiData"]) {
          navigate({ route: "aiData", setQueriesMethod: "replace" });
          return;
        }
        if (dealId) {
          navigate({ route: "dealMonitoringData", params: { deal_id: dealId } });
        } else {
          navigate({ route: "monitoringData", setQueriesMethod: "replace" });
        }
      })
      .catch((e) => {
        pdfTaggerErrorModal(
          ((e as AltiusErrors).errors as ApiValidationErrors) || { errors: [{ message: "Something went wrong!" }] }
        );
        // errorModal({ msg: err?.message || err });
      });
  };

  const handleAnalyzeWithGPT = () => {
    const docIds = documents.map((doc) => doc.id);
    startAnalysis(docIds);
  };

  const handleStopAnalyzeWithGPT = () => {
    setAnalyzingStatus("stopped");
  };

  const handleUndoAnalyzeWithGPT = () => {
    const docIds = documents.map((doc) => doc.id);
    undoAnalysis(docIds);
  };

  const checkNeedsSync = (rows: typeof tableRows): boolean => {
    return rows.some((row) => {
      const rowNeedsSync = row.enabled && row.needs_sync;
      const childrenNeedSync = row.children ? checkNeedsSync(row.children) : false;
      return rowNeedsSync || childrenNeedSync;
    });
  };

  const isEmpty = !tableRows.filter((row) => row.enabled).length;
  const isNeedsSync = checkNeedsSync(tableRows);
  const isSaved = !isEmpty && !isNeedsSync;
  const isSmartTagEnabled = allowGpt && tableRows.some((row) => !row.enabled);

  //if no files are selected, open the file input to select files, and then open load files modal
  useEffect(() => {
    if (!queryParams["filesIds[0]"]) {
      if (isAnalystViewerPermission) {
        openPrompt({ Component: Notice, props: { type: "trial" } });
        return;
      }
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = true;
      input.accept = ".pdf";
      input.onchange = (e: Event) => {
        const files = Array.from((e.target as HTMLInputElement).files || []);
        if (files.length) {
          handleLoadFiles(files);
          // Clear the input value to allow the same file to be selected again
          (e.target as HTMLInputElement).value = "";
        }
      };
      input.click();
    }
  }, []);

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst]}
      RenderInstead={
        <Stack sx={{ bgcolor: "#f6f8fc", p: "7px 70px 0px 70px", alignItems: "center" }}>
          <Typography sx={{ fontSize: "2.5em", color: "#173c4c", fontWeight: 500 }}>
            The content of this page is not available to you
          </Typography>
        </Stack>
      }
    >
      <Stack sx={{ width: "100%", height: "100%", overflow: "auto" }} data-testid="tagger-page">
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginBottom: "15px", width: "100%" }}
        >
          <Stack flexDirection="column" justifyContent="flex-start" alignItems="center" gap="12px">
            {isEmpty ? (
              <Stack flexDirection="row" gap="10px" alignItems="center" justifyContent="center">
                <Typography variant="body2" color="rgba(7, 31, 53, 1)">
                  {documents.length
                    ? "This file needs tagging. Please tag the relevant fields in the document below."
                    : "Upload a file to manage and edit it"}
                </Typography>
                <IconButton size="sm" onClick={() => setHintOpen(true)} ref={hintAnchorRef}>
                  <InfoOutlined sx={{ fontSize: "15px" }} />
                </IconButton>
              </Stack>
            ) : null}
          </Stack>

          <Stack direction="row">
            {!documents.length && (
              <AddFilesButton
                onLoadFiles={
                  isAnalystViewerPermission
                    ? () => openPrompt({ Component: Notice, props: { type: "trial" } })
                    : handleLoadFiles
                }
              />
            )}
            {!!documents.length && (isNeedsSync || isSaved) && (
              <Stack direction="row" alignItems="center" gap="10px" marginRight="14px">
                {isNeedsSync ? <ModeEditOutlineOutlinedIcon /> : <CheckOutlinedIcon />}
                <Typography sx={{ fontSize: "14px", color: "#496682" }}>{isNeedsSync ? "Draft" : "Saved"}</Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
        {isSmartTagEnabled && (
          <SmartTagBar
            onAnalyze={handleAnalyzeWithGPT}
            onStop={handleStopAnalyzeWithGPT}
            status={analyzingStatus}
            onUndo={handleUndoAnalyzeWithGPT}
          />
        )}
        {documents.length > 0 ? (
          <DocumentView
            file={documents[currentDocIndex].file}
            docId={taggerState.documentIds[currentDocIndex]}
            setTableOpen={() => setIsTableOpen(true)}
            disabled={analyzingStatus === "processing"}
            currentDocIndex={currentDocIndex}
            setCurrentDocIndex={setCurrentDocIndex}
          />
        ) : (
          <DocumentView
            file={null}
            docId={0}
            onLoadFiles={handleLoadFiles}
            setTableOpen={() => {}}
            currentDocIndex={0}
            setCurrentDocIndex={setCurrentDocIndex}
          />
        )}
        <TableDrawer
          dealId={dealId}
          isAddTagAvailable={!!documents.length}
          onAddTag={handleAddTag}
          onEditTag={handleEditTag}
          // onDeleteTag={handleDelete}
          onDownload={handleDownload}
          onSave={handleSave}
          open={isTableOpen}
          setOpen={setIsTableOpen}
          isSaveDisabled={!isNeedsSync}
        />
        {newPdfModalOpen && (
          <TagNewPdfModal open={newPdfModalOpen} onClose={() => setNewPdfModalModalOpen(false)} onNext={handleNext} />
        )}
        <ParsingPdfModal
          open={(parsingPdf && !hideParsingModal) || scanningFiles}
          title={scanningFiles ? "Scanning files" : "Parsing PDF"}
          askToNotify={!scanningFiles}
          onClose={() => setHideParsingModal(true)}
          {...(scanningFiles && { text: "Your files are under security scan" })}
        />
        <HintPopover open={hintOpen} anchorEl={hintAnchorRef.current} onClose={() => setHintOpen(false)} />
      </Stack>
    </RoleComponentGuard>
  );
};
